/* eslint-disable @typescript-eslint/adjacent-overload-signatures */
import { Injectable, PipeTransform } from '@angular/core';
import { getFirebaseBackend } from 'src/app/authUtils';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';

import { ListForecastYear } from '../../../core/models/forecast.models';
import { DecimalPipe } from '@angular/common';
import { debounceTime, delay, switchMap, tap } from 'rxjs/operators';
import {
	SortColumnYear,
	SortDirectionYear,
} from '../listjs-sortable-forecast-list.directive';
import { ForecastService } from 'src/app/services/forecast/forecast.service';
import { SSF } from 'xlsx';

interface SearchResult {
	list: ListForecastYear[];
	total: number;
}

export interface State {
	page: number;
	pageSize: number;
	searchTerm: string;
	sortColumn: SortColumnYear;
	sortDirection: SortDirectionYear;
	startIndex: number;
	endIndex: number;
	totalRecords: number;
}
const compare = (v1: string | number, v2: string | number) =>
	v1 < v2 ? -1 : v1 > v2 ? 1 : 0;

function sort(
	list: ListForecastYear[],
	column: SortColumnYear,
	direction: string,
): ListForecastYear[] {
	if (direction === '' || column === '') {
		return list;
	} else {
		return [...list].sort((a, b) => {
			const res = compare(a[column], b[column]);
			return direction === 'asc' ? res : -res;
		});
	}
}

function matches(year: ListForecastYear, term: string, pipe: PipeTransform) {
	return (
		year.id.toLowerCase().includes(term.toLowerCase()) ||
		year.name.toLowerCase().includes(term.toLowerCase()) ||
		year.type.toLowerCase().includes(term.toLowerCase()) ||
		year.lead_cost.toLowerCase().includes(term.toLowerCase()) ||
		year.january.toLowerCase().includes(term.toLowerCase()) ||
		year.february.toLowerCase().includes(term.toLowerCase()) ||
		year.march.toLowerCase().includes(term.toLowerCase()) ||
		year.april.toLowerCase().includes(term.toLowerCase()) ||
		year.may.toLowerCase().includes(term.toLowerCase()) ||
		year.june.toLowerCase().includes(term.toLowerCase()) ||
		year.july.toLowerCase().includes(term.toLowerCase()) ||
		year.august.toLowerCase().includes(term.toLowerCase()) ||
		year.september.toLowerCase().includes(term.toLowerCase()) ||
		year.october.toLowerCase().includes(term.toLowerCase()) ||
		year.november.toLowerCase().includes(term.toLowerCase()) ||
		year.december.toLowerCase().includes(term.toLowerCase()) ||
		year.total_leads.toLowerCase().includes(term.toLowerCase()) ||
		year.total_revenue.toLowerCase().includes(term.toLowerCase())
	);
}

@Injectable({ providedIn: 'root' })
export class ListForecastServiceYear {
	private _loading$ = new BehaviorSubject<boolean>(true);
	private _search$ = new Subject<void>();
	private _list$ = new BehaviorSubject<ListForecastYear[]>([]);
	private _total$ = new BehaviorSubject<number>(0);
	private _listYear$ = new BehaviorSubject<ListForecastYear[]>([]);

	//////
	user: any;
	list: any;
	list$: ListForecastYear[] = [];
	allRows: any;

	private _state: State = {
		page: 1,
		pageSize: 10,
		searchTerm: '',
		sortColumn: '',
		sortDirection: '',
		startIndex: 0,
		endIndex: 9,
		totalRecords: 0,
	};

	constructor(
		private pipe: DecimalPipe,
		private forecastService: ForecastService,
	) {
		this.user = getFirebaseBackend()?.getAuthenticatedUser();

		this.listRows();

		setTimeout(() => {
			this._search$
				.pipe(
					tap(() => this._loading$.next(true)),
					debounceTime(200),
					switchMap(() => this._search()),
					delay(200),
					tap(() => this._loading$.next(false)),
				)
				.subscribe((result) => {
					this._list$.next(result.list);
					this._total$.next(result.total);
				});

			this._search$.next();
		}, 18000);
	}

	get allRows$() {
		return this.allRows;
	}
	get list_result$() {
		return this._list$.asObservable();
	}
	get total$() {
		return this._total$.asObservable();
	}
	get loading$() {
		return this._loading$.asObservable();
	}
	get page() {
		return this._state.page;
	}
	get pageSize() {
		return this._state.pageSize;
	}
	get searchTerm() {
		return this._state.searchTerm;
	}
	get startIndex() {
		return this._state.startIndex;
	}
	get endIndex() {
		return this._state.endIndex;
	}
	get totalRecords() {
		return this._state.totalRecords;
	}
	get list_result_year$() {
		return this._listYear$.asObservable();
	}

	set page(page: number) {
		this._set({ page });
	}
	set pageSize(pageSize: number) {
		this._set({ pageSize });
	}
	set searchTerm(searchTerm: string) {
		this._set({ searchTerm });
	}
	set sortColumn(sortColumn: SortColumnYear) {
		this._set({ sortColumn });
	}
	set sortDirection(sortDirection: SortDirectionYear) {
		this._set({ sortDirection });
	}
	set startIndex(startIndex: number) {
		this._set({ startIndex });
	}
	set endIndex(endIndex: number) {
		this._set({ endIndex });
	}
	set totalRecords(totalRecords: number) {
		this._set({ totalRecords });
	}

	public listRows() {
		this.forecastService.listForecastYear().subscribe((data) => {
			if (data.success) {
				this.list = data.body;
				this.allRows = data.body;
			}
		});
	}

	private _set(patch: Partial<State>) {
		Object.assign(this._state, patch);
		this._search$.next();
	}

	private _search(): Observable<SearchResult> {
		const { sortColumn, sortDirection, pageSize, page, searchTerm } =
			this._state;

		// 1. sort
		let list = sort(this.list, sortColumn, sortDirection);

		// 2. filter
		list = list.filter((row) => matches(row, searchTerm, this.pipe));
		const total = list.length;

		// 3. paginate
		this.totalRecords = list.length;
		this._state.startIndex = (page - 1) * this.pageSize + 1;
		this._state.endIndex = (page - 1) * this.pageSize + this.pageSize;
		if (this.endIndex > this.totalRecords) {
			this.endIndex = this.totalRecords;
		}
		list = list.slice(this._state.startIndex - 1, this._state.endIndex);
		return of({ list, total });
	}
}
